import { QueryClient, QueryFunction, MutationCache, QueryCache } from "@tanstack/react-query";
import { Sentry } from "@/sentry";

const SENTRY_REPORTED = Symbol.for("hms.sentryReported");

type ReportableError = Error & {
  [SENTRY_REPORTED]?: boolean;
  status?: number;
};

function markReported(error: unknown): void {
  if (error && typeof error === "object") {
    Object.defineProperty(error, SENTRY_REPORTED, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  }
}

function alreadyReported(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === "object" &&
      SENTRY_REPORTED in error &&
      (error as ReportableError)[SENTRY_REPORTED],
  );
}

/**
 * Expected client-side failures we do not want in Sentry (auth UX, turnstile UX).
 */
export function shouldReportClientError(error: unknown): boolean {
  if (!(error instanceof Error)) return true;

  const message = error.message;
  if (message.startsWith("401:")) return false;
  if (message.startsWith("422:")) return false;
  if (message.includes("Please complete the verification step")) return false;
  if (message.includes("Verification is temporarily unavailable")) return false;
  if (message.includes("Please complete the verification step.")) return false;

  return true;
}

/**
 * Report a client error to Sentry once. Safe to call from apiRequest,
 * react-query caches, and local try/catch blocks.
 */
export function reportClientError(
  error: unknown,
  context?: Record<string, string>,
): void {
  if (alreadyReported(error) || !shouldReportClientError(error)) {
    return;
  }

  markReported(error);

  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setTag(key, value);
      }
    }
    if (error instanceof Error) {
      const statusMatch = error.message.match(/^(\d{3}):/);
      if (statusMatch) {
        scope.setTag("http_status", statusMatch[1]);
      }
    }
    Sentry.captureException(error);
  });
}

function requestPath(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
  return url.split("?")[0] ?? url;
}

/**
 * A 401 from somewhere other than login or /api/auth/me might be a dead
 * session, or it might be bad input on a still-valid session (for example
 * POST /api/auth/2fa/disable). Callers must confirm with /api/auth/me
 * before dropping the cached user.
 */
export function shouldClearAuthSession(url: string, status: number): boolean {
  if (status !== 401) return false;
  const path = requestPath(url);
  if (path === "/api/auth/login" || path === "/api/auth/me") return false;
  return true;
}

let clearAuthSession: (url: string, status: number) => Promise<void> = async () => {};

async function throwIfResNotOk(
  res: Response,
  source: string,
  url: string,
): Promise<void> {
  if (!res.ok) {
    await clearAuthSession(url, res.status);
    const text = (await res.text()) || res.statusText;
    const error = new Error(`${res.status}: ${text}`) as ReportableError;
    error.status = res.status;
    reportClientError(error, {
      source,
      http_status: String(res.status),
    });
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res, "api_request", url);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      await clearAuthSession(url, res.status);
      return null;
    }

    await throwIfResNotOk(res, "query_fn", url);
    return await res.json();
  };

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      reportClientError(error, { source: "react_query_mutation" });
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      reportClientError(error, { source: "react_query_query" });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export function invalidateSiteAuditQueries() {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      return typeof key === "string" && key.startsWith("/api/site-audit");
    },
  });
}

clearAuthSession = async (url, status) => {
  if (!shouldClearAuthSession(url, status)) return;

  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (res.status === 401) {
      queryClient.setQueryData(["/api/auth/me"], null);
      return;
    }
    if (!res.ok) return;
    queryClient.setQueryData(["/api/auth/me"], await res.json());
  } catch {
    // Leave the cached session in place when the check itself fails.
  }
};
